import { useState } from 'react';
import { demander } from '@/components/ui/Fenetres';
import { AlertTriangle, CheckCircle2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Topbar } from '@/components/layout/Topbar';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/stores/toastStore';
import { paieApi, type ContenuReglesPaie, type ParametresSortie, type Rubrique, type TraitementIndemnite, type VersionRegles } from '@/api/paie';
import { PaieBulletins } from './PaieBulletins';
import { ComptesPaie } from './ComptesPaie';

/**
 * PAIE-2 — paramètres légaux de paie et rubriques (docs/PLAN-PAIE.md).
 * Valeurs préremplies « à confirmer » ; une valeur vide = non renseignée. Une version confirmée (par un professionnel de
 * la paie) est figée ; une correction = une nouvelle version datée. Aucun bulletin réel tant que ce n'est pas confirmé.
 */

type Champ = { chemin: string; libelle: string; unite: '%' | 'FCFA' | 'ans' | 'parts' | 'jours' };
const GROUPES: { titre: string; champs: Champ[] }[] = [
  { titre: 'Salaire minimum', champs: [{ chemin: 'smigFcfa', libelle: 'SMIG mensuel', unite: 'FCFA' }] },
  { titre: 'CNPS', champs: [
    { chemin: 'cnps.retraiteSalariePct', libelle: 'Retraite — part salarié', unite: '%' },
    { chemin: 'cnps.retraitePatronalePct', libelle: 'Retraite — part employeur', unite: '%' },
    { chemin: 'cnps.plafondRetraiteFcfa', libelle: 'Retraite — plafond mensuel', unite: 'FCFA' },
    { chemin: 'cnps.prestationsFamilialesPct', libelle: 'Prestations familiales — employeur', unite: '%' },
    { chemin: 'cnps.maternitePct', libelle: 'Maternité — employeur', unite: '%' },
    { chemin: 'cnps.accidentTravailPct', libelle: 'Accident du travail — employeur', unite: '%' },
    { chemin: 'cnps.plafondPfAtFcfa', libelle: 'Plafond prestations familiales, maternité, accident', unite: 'FCFA' },
  ] },
  { titre: 'Couverture maladie universelle (CMU)', champs: [
    { chemin: 'cmu.salarieFcfa', libelle: 'Part salarié (par mois)', unite: 'FCFA' },
    { chemin: 'cmu.patronaleFcfa', libelle: 'Part employeur (par mois)', unite: 'FCFA' },
  ] },
  { titre: 'Contributions de l’employeur', champs: [
    { chemin: 'patronales.taxeApprentissagePct', libelle: 'Taxe d’apprentissage', unite: '%' },
    { chemin: 'patronales.formationContinuePct', libelle: 'Formation professionnelle continue', unite: '%' },
    { chemin: 'patronales.contributionEmployeurItsPct', libelle: 'Contribution employeur sur salaires', unite: '%' },
  ] },
  { titre: 'Prime d’ancienneté (convention interprofessionnelle)', champs: [
    { chemin: 'anciennete.apresAnnees', libelle: 'À partir de', unite: 'ans' },
    { chemin: 'anciennete.tauxInitialPct', libelle: 'Taux de départ', unite: '%' },
    { chemin: 'anciennete.parAnneeSupplementairePct', libelle: 'Par année en plus', unite: '%' },
    { chemin: 'anciennete.plafondPct', libelle: 'Plafond', unite: '%' },
  ] },
  { titre: 'Indemnité de transport', champs: [{ chemin: 'transport.exonereJusquaFcfa', libelle: 'Exonérée jusqu’à (par mois)', unite: 'FCFA' }] },
  // PAIE-3 (19/10/2026)
  { titre: 'Impôt sur les salaires — base et nombre de parts', champs: [
    { chemin: 'calculIts.abattementPct', libelle: 'Abattement forfaitaire sur la base', unite: '%' },
    { chemin: 'parts.celibataire', libelle: 'Parts — célibataire, divorcé(e), veuf/veuve', unite: 'parts' },
    { chemin: 'parts.celibataireAvecEnfants', libelle: 'Parts — non marié(e) avec enfant(s), base', unite: 'parts' },
    { chemin: 'parts.marie', libelle: 'Parts — marié(e)', unite: 'parts' },
    { chemin: 'parts.parEnfant', libelle: 'Parts — par enfant à charge', unite: 'parts' },
    { chemin: 'parts.maximum', libelle: 'Parts — plafond', unite: 'parts' },
  ] },
  { titre: 'Entrée ou sortie en cours de mois', champs: [{ chemin: 'prorata.diviseurJours', libelle: 'Salaire × jours sous contrat ÷', unite: 'jours' }] },
];

const lire = (o: any, c: string) => c.split('.').reduce((v, k) => (v == null ? undefined : v[k]), o);
const ecrire = (o: any, c: string, val: number | null) => {
  const copie = structuredClone(o); const k = c.split('.'); let x = copie;
  k.slice(0, -1).forEach((p) => { x[p] = x[p] ?? {}; x = x[p]; }); x[k[k.length - 1]] = val; return copie;
};
const nombre = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));
const dateFr = (iso: string) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('fr-FR', { timeZone: 'UTC' });
const fcfa = (n: number | null) => (n == null ? '—' : new Intl.NumberFormat('fr-FR').format(n));

export const PaieParametresPage = () => {
  const role = useAuthStore((s) => s.user?.role);
  const estDaf = role === 'daf' || role === 'admin_holding';
  const qc = useQueryClient();
  const versions = useQuery({ queryKey: ['paie', 'regles'], queryFn: paieApi.regles });
  const [choix, setChoix] = useState<string | null>(null);
  const [onglet, setOnglet] = useState<'bulletins' | 'parametres'>('bulletins');
  const v = versions.data?.find((x) => x.id === choix) ?? versions.data?.find((x) => x.enVigueur) ?? versions.data?.[0] ?? null;

  return (
    <>
      <Topbar title="Paie" subtitle={onglet === 'bulletins' ? 'Bulletins du mois, livre de paie' : 'Taux et barèmes légaux, rubriques de paie'}
        actions={<button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void versions.refetch()} aria-label="Rafraîchir"><RefreshCw size={14} /></button>} />
      <div className="ds-content" style={{ display: 'grid', gap: 16 }}>
        <div className="ds-rp-tabs" style={{ maxWidth: 360 }}>
          <button className={`ds-rp-tab${onglet === 'bulletins' ? ' on' : ''}`} onClick={() => setOnglet('bulletins')}>Bulletins</button>
          <button className={`ds-rp-tab${onglet === 'parametres' ? ' on' : ''}`} onClick={() => setOnglet('parametres')}>Paramètres</button>
        </div>
        {onglet === 'bulletins' && <PaieBulletins />}
        {onglet === 'parametres' && <>
        {versions.data && versions.data.length > 1 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, fontSize: 12 }}>
            {versions.data.map((x) => (
              <button key={x.id} className={`ds-btn ds-btn-sm ${x.id === v?.id ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setChoix(x.id)}>
                À partir du {dateFr(x.valableDu)} {x.statut === 'confirmee' ? '✓' : '(à confirmer)'}{x.enVigueur ? ' · en vigueur' : ''}
              </button>
            ))}
          </div>
        )}
        {versions.isLoading ? 'Chargement…' : v ? (
          <Version key={v.id + v.statut} v={v} estDaf={estDaf} onChange={() => qc.invalidateQueries({ queryKey: ['paie', 'regles'] })} onNouvelle={(id) => setChoix(id)} />
        ) : <p style={{ color: 'var(--tx-3)' }}>Aucun paramètre de paie.</p>}
        <Rubriques estDaf={estDaf} />
        {estDaf && <ComptesPaie />}
        </>}
      </div>
    </>
  );
};

const Version = ({ v, estDaf, onChange, onNouvelle }: { v: VersionRegles; estDaf: boolean; onChange: () => void; onNouvelle: (id: string) => void }) => {
  const [c, setC] = useState<ContenuReglesPaie>(v.contenu);
  const modifiable = estDaf && v.statut === 'a_confirmer';
  const modifie = JSON.stringify(c) !== JSON.stringify(v.contenu);
  const enregistrer = useMutation({ mutationFn: () => paieApi.modifierVersion(v.id, c), onSuccess: () => { toast.success('Paramètres enregistrés'); onChange(); } });
  const confirmer = useMutation({ mutationFn: (source: string) => paieApi.confirmer(v.id, source), onSuccess: () => { toast.success('Version confirmée', 'Elle est désormais figée'); onChange(); } });
  const supprimer = useMutation({ mutationFn: () => paieApi.supprimer(v.id), onSuccess: () => { toast.success('Version supprimée'); onChange(); } });
  const nouvelle = useMutation({
    mutationFn: (jour: string) => paieApi.creerVersion(jour, c),
    onSuccess: (liste) => { toast.success('Nouvelle version créée'); onChange(); const n = liste.find((x) => x.statut === 'a_confirmer' && x.id !== v.id); if (n) onNouvelle(n.id); },
  });
  const demanderConfirmation = async () => {
    const s = await demander.texte({ titre: 'Qui a validé ces paramètres ?', message: 'Ex. « Cabinet X, courrier du 25/10/2026 »', min: 3, confirmer: 'Confirmer les paramètres', danger: true });
    if (s === null) return;
    confirmer.mutate(s);
  };
  const demanderNouvelle = async () => {
    const j = await demander.texte({ titre: 'Nouvelle version', libelle: 'À partir du (AAAA-MM-JJ)', defaut: new Date().toISOString().slice(0, 10), confirmer: 'Créer la version' });
    if (j && /^\d{4}-\d{2}-\d{2}$/.test(j)) nouvelle.mutate(j);
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {v.statut === 'a_confirmer' ? (
        <div style={{ background: '#FEF3C7', color: '#78350F', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          <strong><AlertTriangle size={14} style={{ verticalAlign: -2 }} /> À CONFIRMER</strong> — valeurs préremplies d’après l’usage, à faire valider par un
          professionnel de la paie. Aucun bulletin réel ne sera produit tant que cette version n’est pas confirmée.
          {v.valeursVides.length > 0 && <div style={{ marginTop: 6 }}>Non renseignées : {v.valeursVides.join(' · ')}.</div>}
        </div>
      ) : (
        <div style={{ background: '#D1FAE5', color: '#064E3B', padding: '10px 14px', borderRadius: 8, fontSize: 13 }}>
          <CheckCircle2 size={14} style={{ verticalAlign: -2 }} /> Confirmée le {v.confirmeLe ? new Date(v.confirmeLe).toLocaleDateString('fr-FR') : ''} par {v.confirmePar} — source : {v.sourceConfirmation}. Version figée.
        </div>
      )}

      <div className="ds-table-panel">
        <div className="ds-table-header">
          <div><div className="ds-table-title">Paramètres à partir du {dateFr(v.valableDu)}</div>
            <div className="ds-table-sub">Montants en FCFA, taux en %. Laisser vide une valeur non connue.</div></div>
          {estDaf && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {modifiable && <button className="ds-btn ds-btn-p ds-btn-sm" disabled={!modifie || enregistrer.isPending} onClick={() => enregistrer.mutate()}><Save size={13} /> Enregistrer</button>}
              {modifiable && <button className="ds-btn ds-btn-g ds-btn-sm" disabled={modifie || confirmer.isPending} title={modifie ? 'Enregistrez d’abord' : ''} onClick={demanderConfirmation}><CheckCircle2 size={13} /> Confirmer</button>}
              <button className="ds-btn ds-btn-g ds-btn-sm" disabled={nouvelle.isPending} onClick={demanderNouvelle}><Plus size={13} /> Nouvelle version</button>
              {modifiable && <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Supprimer cette version" onClick={async () => { if (await demander.confirmer({ titre: 'Supprimer cette version non confirmée ?', confirmer: 'Supprimer', danger: true })) supprimer.mutate(); }}><Trash2 size={13} /></button>}
            </div>
          )}
        </div>
        <div style={{ padding: 16, display: 'grid', gap: 16 }}>
          {GROUPES.map((g) => (
            <div key={g.titre}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{g.titre}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
                {g.champs.map((ch) => {
                  const val = lire(c, ch.chemin) as number | null;
                  return (
                    <label key={ch.chemin} style={{ fontSize: 12 }}>{ch.libelle} ({ch.unite})
                      <input className="ds-fi" inputMode="decimal" disabled={!modifiable} value={val ?? ''} placeholder="non renseigné"
                        style={val == null ? { borderColor: '#F59E0B' } : undefined}
                        onChange={(e) => setC(ecrire(c, ch.chemin, nombre(e.target.value)))} />
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <label style={{ fontSize: 12, maxWidth: 420 }}>Impôt — cotisations salariales déduites de la base
            <select className="ds-fi" disabled={!modifiable} value={c.calculIts?.deduireCotisationsSalariales == null ? '' : String(c.calculIts.deduireCotisationsSalariales)}
              style={c.calculIts?.deduireCotisationsSalariales == null ? { borderColor: '#F59E0B' } : undefined}
              onChange={(e) => setC({ ...c, calculIts: { abattementPct: c.calculIts?.abattementPct ?? null, deduireCotisationsSalariales: e.target.value === '' ? null : e.target.value === 'true' } })}>
              <option value="">non renseigné</option><option value="true">Oui</option><option value="false">Non</option>
            </select>
          </label>
          <BaremeIts c={c} setC={setC} modifiable={modifiable} />
          <Sortie c={c} setC={setC} modifiable={modifiable} />
        </div>
      </div>
    </div>
  );
};

const BaremeIts = ({ c, setC, modifiable }: { c: ContenuReglesPaie; setC: (c: ContenuReglesPaie) => void; modifiable: boolean }) => {
  const tranches = c.its.tranches;
  const maj = (i: number, k: 'jusquaFcfa' | 'tauxPct', s: string) => {
    const t = tranches.map((x, j) => (j === i ? { ...x, [k]: k === 'tauxPct' ? Number(s || 0) : nombre(s) } : x));
    setC({ ...c, its: { ...c.its, tranches: t } });
  };
  const rcf = c.its.reductionChargesFamille;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Impôt sur les salaires — barème mensuel</div>
        <table className="ds-tbl" style={{ maxWidth: 460 }}>
          <thead><tr><th>De (FCFA)</th><th>Jusqu’à (FCFA)</th><th>Taux (%)</th></tr></thead>
          <tbody>
            {tranches.map((t, i) => (
              <tr key={i}>
                <td>{fcfa(i === 0 ? 0 : (tranches[i - 1].jusquaFcfa ?? 0) + 1)}</td>
                <td>{i === tranches.length - 1 ? 'au-delà' : <input className="ds-fi" disabled={!modifiable} value={t.jusquaFcfa ?? ''} onChange={(e) => maj(i, 'jusquaFcfa', e.target.value)} />}</td>
                <td><input className="ds-fi" disabled={!modifiable} value={t.tauxPct} onChange={(e) => maj(i, 'tauxPct', e.target.value)} style={{ width: 80 }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Réduction pour charges de famille</div>
        {rcf == null ? (
          <div style={{ color: '#B45309' }}>Non renseignée (selon le nombre de parts : à fournir par le cabinet).
            {modifiable && <> <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => setC({ ...c, its: { ...c.its, reductionChargesFamille: [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5].map((parts) => ({ parts, montantFcfa: 0 })) } })}>Saisir le tableau</button></>}
          </div>
        ) : (
          <table className="ds-tbl" style={{ maxWidth: 320 }}>
            <thead><tr><th>Parts</th><th>Réduction mensuelle (FCFA)</th></tr></thead>
            <tbody>{rcf.map((r, i) => (
              <tr key={r.parts}><td>{r.parts}</td><td><input className="ds-fi" disabled={!modifiable} value={r.montantFcfa}
                onChange={(e) => setC({ ...c, its: { ...c.its, reductionChargesFamille: rcf.map((x, j) => (j === i ? { ...x, montantFcfa: Number(e.target.value || 0) } : x)) } })} /></td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Rubriques = ({ estDaf }: { estDaf: boolean }) => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['paie', 'rubriques'], queryFn: paieApi.rubriques });
  const maj = useMutation({ mutationFn: ({ id, r }: { id: string; r: Partial<Rubrique> }) => paieApi.modifierRubrique(id, r), onSuccess: () => qc.invalidateQueries({ queryKey: ['paie', 'rubriques'] }) });
  const ajouter = useMutation({
    mutationFn: (r: { code: string; libelle: string; sens: 'gain' | 'retenue' }) => paieApi.creerRubrique({ ...r, soumiseCnps: true, imposable: true }),
    onSuccess: () => { toast.success('Rubrique ajoutée'); void qc.invalidateQueries({ queryKey: ['paie', 'rubriques'] }); },
  });
  const demanderAjout = async () => {
    const libelle = await demander.texte({ titre: 'Nouvelle rubrique', libelle: 'Libellé (ex. Prime de panier)', min: 2, confirmer: 'Continuer' });
    if (libelle === null) return;
    const code = await demander.texte({ titre: 'Nouvelle rubrique', libelle: 'Code court (lettres, chiffres, _)', defaut: libelle.normalize('NFD').replace(/[^A-Za-z0-9]/g, '').slice(0, 10).toUpperCase(), confirmer: 'Continuer' });
    if (!code) return;
    const gain = await demander.confirmer({ titre: 'Est-ce un gain pour le salarié ?', message: '« Gain » = un gain. « Annuler » = une retenue.', confirmer: 'Gain' });
    ajouter.mutate({ code, libelle, sens: gain ? 'gain' : 'retenue' });
  };
  const CALCUL: Record<string, string> = { saisie: 'montant saisi', anciennete: 'calculée (ancienneté)', pointage_hs: 'pointage (heures sup.)', pointage_absence: 'pointage (absences)' };
  return (
    <div className="ds-table-panel">
      <div className="ds-table-header">
        <div><div className="ds-table-title">Rubriques de paie</div><div className="ds-table-sub">Soumission CNPS et impôt proposées selon l’usage : à confirmer.</div></div>
        {estDaf && <button className="ds-btn ds-btn-p ds-btn-sm" onClick={demanderAjout}><Plus size={13} /> Rubrique</button>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="ds-tbl" style={{ minWidth: 640 }}>
          <thead><tr><th>Code</th><th>Libellé</th><th>Sens</th><th>Calcul</th><th>Soumise CNPS</th><th>Imposable</th><th>Active</th><th /></tr></thead>
          <tbody>
            {q.data?.map((r) => (
              <tr key={r.id} style={!r.active ? { opacity: 0.5 } : undefined}>
                <td><span className="ds-ref-code">{r.code}</span></td>
                <td>{r.libelle}</td>
                <td>{r.sens === 'gain' ? 'Gain' : 'Retenue'}</td>
                <td style={{ fontSize: 12 }}>{CALCUL[r.calcul]}</td>
                <td><input type="checkbox" disabled={!estDaf} checked={r.soumiseCnps} onChange={(e) => maj.mutate({ id: r.id, r: { soumiseCnps: e.target.checked } })} /></td>
                <td><input type="checkbox" disabled={!estDaf} checked={r.imposable} onChange={(e) => maj.mutate({ id: r.id, r: { imposable: e.target.checked } })} /></td>
                <td><input type="checkbox" disabled={!estDaf} checked={r.active} onChange={(e) => maj.mutate({ id: r.id, r: { active: e.target.checked } })} /></td>
                <td>{r.aConfirmer
                  ? (estDaf ? <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => maj.mutate({ id: r.id, r: { aConfirmer: false } })}>Confirmer</button> : <span className="ds-bd ds-bd-w">à confirmer</span>)
                  : <span className="ds-bd ds-bd-s">confirmée</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** PAIE-4 — paramètres du solde de tout compte. Tout est vide par défaut : valeurs à fournir par le cabinet. */
const SORTIE_VIDE: ParametresSortie = { congesDiviseurJours: null, preavis: null, licenciementTranches: null, finCddPct: null, traitement: null };
const TRAITEMENT_VIDE: TraitementIndemnite = { soumiseCnps: null, imposable: null };
const INDEMNITES: { k: 'conges' | 'preavis' | 'licenciement' | 'finCdd'; libelle: string }[] = [
  { k: 'conges', libelle: 'Congés payés' }, { k: 'preavis', libelle: 'Préavis' }, { k: 'licenciement', libelle: 'Licenciement' }, { k: 'finCdd', libelle: 'Fin de CDD' },
];
const ouiNon = (v: boolean | null) => (v == null ? '' : String(v));
const lireOuiNon = (s: string) => (s === '' ? null : s === 'true');

const Sortie = ({ c, setC, modifiable }: { c: ContenuReglesPaie; setC: (c: ContenuReglesPaie) => void; modifiable: boolean }) => {
  const so: ParametresSortie = { ...SORTIE_VIDE, ...(c.sortie ?? {}) };
  const maj = (x: Partial<ParametresSortie>) => setC({ ...c, sortie: { ...so, ...x } });
  const orange = (v: unknown) => (v == null ? { borderColor: '#F59E0B' } : undefined);
  const tr = so.licenciementTranches; const pv = so.preavis;
  const traitement = so.traitement ?? { conges: TRAITEMENT_VIDE, preavis: TRAITEMENT_VIDE, licenciement: TRAITEMENT_VIDE, finCdd: TRAITEMENT_VIDE };
  return (
    <div style={{ display: 'grid', gap: 12, fontSize: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 13 }}>Solde de tout compte (départ d’un salarié)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 8 }}>
        <label>Congés payés : salaire de référence ÷ (jours)
          <input className="ds-fi" inputMode="decimal" disabled={!modifiable} value={so.congesDiviseurJours ?? ''} placeholder="non renseigné" style={orange(so.congesDiviseurJours)}
            onChange={(e) => maj({ congesDiviseurJours: nombre(e.target.value) })} /></label>
        <label>Fin de CDD : % des salaires bruts du contrat
          <input className="ds-fi" inputMode="decimal" disabled={!modifiable} value={so.finCddPct ?? ''} placeholder="non renseigné" style={orange(so.finCddPct)}
            onChange={(e) => maj({ finCddPct: nombre(e.target.value) })} /></label>
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Indemnité de licenciement — % du salaire de référence par année d’ancienneté</div>
        {tr == null ? (
          <div style={{ color: '#B45309' }}>Non renseignée.
            {modifiable && <> <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => maj({ licenciementTranches: [{ jusquaAnnees: null, pct: 0 }] })}>Saisir les tranches</button></>}</div>
        ) : (
          <table className="ds-tbl" style={{ maxWidth: 460 }}>
            <thead><tr><th>Jusqu’à (années)</th><th>% par année</th><th /></tr></thead>
            <tbody>{tr.map((t, i) => (
              <tr key={i}>
                <td>{i === tr.length - 1 ? 'au-delà' : <input className="ds-fi" disabled={!modifiable} value={t.jusquaAnnees ?? ''}
                  onChange={(e) => maj({ licenciementTranches: tr.map((x, j) => (j === i ? { ...x, jusquaAnnees: nombre(e.target.value) } : x)) })} />}</td>
                <td><input className="ds-fi" disabled={!modifiable} value={t.pct} style={{ width: 80 }}
                  onChange={(e) => maj({ licenciementTranches: tr.map((x, j) => (j === i ? { ...x, pct: nombre(e.target.value) ?? 0 } : x)) })} /></td>
                <td>{modifiable && <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Retirer la tranche"
                  onClick={() => maj({ licenciementTranches: tr.length > 1 ? tr.filter((_, j) => j !== i).map((x, j, a) => (j === a.length - 1 ? { ...x, jusquaAnnees: null } : x)) : null })}><Trash2 size={12} /></button>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {modifiable && tr != null && <button className="ds-btn ds-btn-g ds-btn-sm" style={{ marginTop: 4 }}
          onClick={() => maj({ licenciementTranches: [...tr.slice(0, -1), { jusquaAnnees: 0, pct: 0 }, tr[tr.length - 1]] })}><Plus size={12} /> Ajouter une tranche</button>}
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Durée du préavis (mois) selon la catégorie et l’ancienneté</div>
        {pv == null ? (
          <div style={{ color: '#B45309' }}>Non renseignée (sans tableau, la durée est saisie au départ du salarié).
            {modifiable && <> <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => maj({ preavis: [{ categories: [], ancienneteMinAnnees: 0, mois: 0 }] })}>Saisir le tableau</button></>}</div>
        ) : (
          <table className="ds-tbl" style={{ maxWidth: 560 }}>
            <thead><tr><th>Catégories (séparées par des virgules, vide = toutes)</th><th>À partir de (années)</th><th>Mois</th><th /></tr></thead>
            <tbody>{pv.map((l, i) => (
              <tr key={i}>
                <td><input className="ds-fi" disabled={!modifiable} value={l.categories.join(', ')}
                  onChange={(e) => maj({ preavis: pv.map((x, j) => (j === i ? { ...x, categories: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) } : x)) })} /></td>
                <td><input className="ds-fi" disabled={!modifiable} value={l.ancienneteMinAnnees} style={{ width: 70 }}
                  onChange={(e) => maj({ preavis: pv.map((x, j) => (j === i ? { ...x, ancienneteMinAnnees: nombre(e.target.value) ?? 0 } : x)) })} /></td>
                <td><input className="ds-fi" disabled={!modifiable} value={l.mois} style={{ width: 70 }}
                  onChange={(e) => maj({ preavis: pv.map((x, j) => (j === i ? { ...x, mois: nombre(e.target.value) ?? 0 } : x)) })} /></td>
                <td>{modifiable && <button className="ds-btn ds-btn-g ds-btn-sm" aria-label="Retirer la ligne" onClick={() => maj({ preavis: pv.length > 1 ? pv.filter((_, j) => j !== i) : null })}><Trash2 size={12} /></button>}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {modifiable && pv != null && <button className="ds-btn ds-btn-g ds-btn-sm" style={{ marginTop: 4 }} onClick={() => maj({ preavis: [...pv, { categories: [], ancienneteMinAnnees: 0, mois: 0 }] })}><Plus size={12} /> Ajouter une ligne</button>}
      </div>

      <div>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Traitement des indemnités (non renseigné = comptée soumise et imposable par prudence)</div>
        <table className="ds-tbl" style={{ maxWidth: 460 }}>
          <thead><tr><th>Indemnité</th><th>Soumise CNPS</th><th>Imposable</th></tr></thead>
          <tbody>{INDEMNITES.map(({ k, libelle }) => (
            <tr key={k}><td>{libelle}</td>
              {(['soumiseCnps', 'imposable'] as const).map((champ) => (
                <td key={champ}><select className="ds-fi" disabled={!modifiable} value={ouiNon(traitement[k][champ])} style={orange(traitement[k][champ])}
                  onChange={(e) => maj({ traitement: { ...traitement, [k]: { ...traitement[k], [champ]: lireOuiNon(e.target.value) } } })}>
                  <option value="">non renseigné</option><option value="true">Oui</option><option value="false">Non</option>
                </select></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
